import { useState, useEffect, useCallback } from 'react';
import { tipoEntradaApi } from '../api/tipoEntradaApi';
import { showErrorToast, showSuccessToast } from '../utils/errorHandler';

/**
 * Hook personalizado para gestión de tipos de entrada
 * Proporciona estado y funciones para operaciones CRUD de tipos de entrada
 */
export const useTipoEntradas = () => {
  const [tipoEntradas, setTipoEntradas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Obtiene todos los tipos de entrada
   */
  const fetchTipoEntradas = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await tipoEntradaApi.getAll();
      setTipoEntradas(response.data);
      return response.data;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al cargar tipos de entrada');
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Obtiene el tipo de entrada automático (código 'A')
   */
  const fetchTipoEntradaAutomatico = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await tipoEntradaApi.getAutomatico();
      return response.data;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al cargar tipo de entrada automático');
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Obtiene un tipo de entrada específico por ID
   */
  const fetchTipoEntradaById = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await tipoEntradaApi.getById(id);
      return response.data;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al cargar el tipo de entrada');
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Crea un nuevo tipo de entrada
   */
  const createTipoEntrada = useCallback(async (data) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await tipoEntradaApi.create(data);
      showSuccessToast('Tipo de entrada creado exitosamente');
      
      // Refrescar la lista
      await fetchTipoEntradas();
      
      return response.data;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al crear el tipo de entrada');
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchTipoEntradas]);

  /**
   * Actualiza un tipo de entrada existente
   */
  const updateTipoEntrada = useCallback(async (id, data) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await tipoEntradaApi.update(id, data);
      showSuccessToast('Tipo de entrada actualizado exitosamente');
      
      // Refrescar la lista
      await fetchTipoEntradas();
      
      return response.data;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al actualizar el tipo de entrada');
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchTipoEntradas]);

  /**
   * Elimina un tipo de entrada
   */
  const deleteTipoEntrada = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    
    try {
      await tipoEntradaApi.delete(id);
      showSuccessToast('Tipo de entrada eliminado exitosamente');
      
      // Refrescar la lista
      await fetchTipoEntradas();
      
      return true;
    } catch (err) {
      const errorMessage = showErrorToast(err, 'Error al eliminar el tipo de entrada');
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchTipoEntradas]);

  // Nota: La carga de tipos de entrada es manual.
  // Usar fetchTipoEntradas() cuando sea necesario en lugar de carga automática.

  return {
    // Estado
    tipoEntradas,
    loading,
    error,
    
    // Funciones
    fetchTipoEntradas,
    fetchTipoEntradaAutomatico,
    fetchTipoEntradaById,
    createTipoEntrada,
    updateTipoEntrada,
    deleteTipoEntrada,
    
    // Función para limpiar errores
    clearError: () => setError(null),
  };
};

export default useTipoEntradas;
